import { useEffect, useMemo, useState } from 'react';

import { AlertCircle, Image as ImageIcon } from 'lucide-react';

import type { StorageFolder } from '@/domain/shared/media';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchSignedMediaUrls, selectMediaByKeys } from '@/features/media';

type MediaThumbnailGridProps = {
  propertyID: string;
  folder: StorageFolder;
  imageKeys: string[];
  className?: string;
  itemClassName?: string;
  altPrefix?: string;
};

function normalizeKeys(keys: string[]): string[] {
  return Array.from(new Set(keys.map((key) => key?.trim()).filter((key): key is string => Boolean(key))));
}

export function MediaThumbnailGrid({
  propertyID,
  folder,
  imageKeys,
  className,
  itemClassName,
  altPrefix = 'Attachment',
}: MediaThumbnailGridProps) {
  const dispatch = useAppDispatch();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const normalizedKeys = useMemo(() => normalizeKeys(imageKeys), [imageKeys]);
  const mediaItems = useAppSelector((state) => selectMediaByKeys(state, normalizedKeys));

  const mediaByKey = useMemo(() => {
    return new Map(mediaItems.map((item) => [item.key, item]));
  }, [mediaItems]);

  useEffect(() => {
    if (!propertyID || normalizedKeys.length === 0) return;

    void dispatch(
      fetchSignedMediaUrls({
        propertyID,
        folder,
        filesKeys: normalizedKeys,
      }),
    );
  }, [dispatch, folder, normalizedKeys, propertyID]);

  if (normalizedKeys.length === 0) {
    return null;
  }

  const selectedMedia = selectedKey ? (mediaByKey.get(selectedKey) ?? null) : null;
  const canPreviewSelected = Boolean(selectedMedia?.url && !selectedMedia.mediaNotFoundOrCorrupt);

  return (
    <>
      <div className={cn('flex flex-wrap gap-3', className)}>
        {normalizedKeys.map((key, index) => {
          const media = mediaByKey.get(key);
          const isExpired = Boolean(media?.expiresAt && media.expiresAt <= Date.now());
          const isLoading = !media || media.loading || isExpired;
          const isError = Boolean(media?.mediaNotFoundOrCorrupt || media?.error);
          const canPreview = Boolean(media?.url && !media.mediaNotFoundOrCorrupt);

          return (
            <button
              key={key}
              type="button"
              onClick={() => canPreview && setSelectedKey(key)}
              disabled={!canPreview}
              className={cn(
                'group bg-muted/30 relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-md border text-left transition',
                canPreview ? 'hover:border-foreground/30 cursor-pointer' : 'cursor-default',
                itemClassName,
              )}
            >
              {isLoading ? (
                <Skeleton className="h-full w-full rounded-none" />
              ) : isError ? (
                <div className="text-muted-foreground flex h-full w-full flex-col items-center justify-center gap-1 p-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-center text-[10px] leading-tight">Image unavailable</span>
                </div>
              ) : (
                <img
                  src={media.url}
                  alt={`${altPrefix} ${index + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              )}

              {!isLoading && !isError && canPreview ? (
                <div className="bg-background/70 text-foreground absolute inset-x-0 bottom-0 flex items-center gap-1 px-2 py-1 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
                  <ImageIcon className="h-3 w-3" />
                  <span>Preview</span>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      <Dialog open={Boolean(selectedKey && canPreviewSelected)} onOpenChange={(open) => !open && setSelectedKey(null)}>
        <DialogContent className="max-h-[calc(100vh-2rem)] max-w-5xl border-0 bg-black/95 p-3 sm:p-4">
          {selectedMedia?.url ? (
            <img
              src={selectedMedia.url}
              alt={selectedKey ?? 'Attachment preview'}
              className="max-h-[calc(100vh-6rem)] w-full rounded-md object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
